import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "use"];
import { useContext } from 'react';
import styled from 'foundations-theming/utils';
import { StyledText } from '../../../components/Text/Text';
import { DialogHeaderContext } from './DialogHeaderContext';
import { DialogContext } from '../Dialog/DialogContext';
import { MODAL_H1_USES } from '../ModalDialog/ModalDialog';
import { filterProps } from '../../../utils/filterProps';
import { getDialogHeaderBackground } from '../Dialog/Dialog';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledBadge } from '../Badge/Badge';
import { StyledClickable } from '../Clickable/Clickable';
import { jsx as _jsx } from "react/jsx-runtime";
// Determines whether a dialog should have rounded top borders based on the modal use.
// Sidebar and fullscreen modals should have square corners to match their container.
const shouldHaveRoundedBorders = modalUse => {
  return modalUse !== 'sidebar' && modalUse !== 'fullscreen';
};
export const StyledDialogHeader = styled.header.withConfig({
  displayName: "DialogHeader__StyledDialogHeader"
})(({
  $use,
  $cssStyles,
  theme
}) => {
  const modalUse = useContext(DialogContext).use;
  const isModalH1Use = MODAL_H1_USES.includes(modalUse);
  const isConversational = modalUse === 'conversational';
  const isCenteredUse = modalUse === 'conversational' || modalUse === 'info' || modalUse === 'upgrades';
  const isFullscreen = modalUse === 'fullscreen';
  const isSuccess = modalUse === 'success';
  const isInfo = modalUse === 'info';
  const isDanger = modalUse === 'danger';
  const isUpgrades = modalUse === 'upgrades';

  // Dialog also renders the background color styling, so in the case of these
  // modal types, we want to fallback to whatever Dialog is rendering. In other
  // cases, we need the background styling as DialogHeader could be rendered outside
  // the context of a Dialog (e.g WizardHeader)
  const isTransparent = isSuccess || isInfo || isDanger || isUpgrades;
  const getBackground = () => {
    if (isTransparent) {
      return {
        background: theme.color['fill-transparent-default']
      };
    }
    if (isFullscreen) {
      return {
        backgroundColor: theme.color['fill-surface-default'],
        backgroundImage: `linear-gradient(86deg, ${theme.color['fill-transitional-modal-gradient-color-1']} -3.28%, ${theme.color['fill-transitional-modal-gradient-color-2']} 97.8%)`
      };
    }
    return getDialogHeaderBackground({
      theme,
      use: modalUse
    });
  };
  return Object.assign({
    color: modalUse !== 'conversational' ? theme.color['text-inverse-default'] : undefined,
    [`& ${StyledText}:not(${StyledBadge} ${StyledText}):not(h2),
          & h1,
          & h3,
          & h4,
          & h5,
          & h6`]: {
      color: modalUse !== 'conversational' ? theme.color['text-inverse-default'] : undefined,
      fontSize: theme.typography['heading-300'].fontSize,
      fontWeight: theme.typography['heading-300'].fontWeight,
      lineHeight: theme.typography['heading-300'].lineHeight
    },
    [`& h2`]: {
      color: modalUse !== 'conversational' ? theme.color['text-inverse-default'] : undefined,
      fontSize: isModalH1Use ? theme.typography['heading-600'].fontSize : theme.typography['heading-300'].fontSize,
      fontWeight: isModalH1Use ? theme.typography['heading-600'].fontWeight : theme.typography['heading-300'].fontWeight,
      lineHeight: isModalH1Use ? theme.typography['heading-600'].lineHeight : theme.typography['heading-300'].lineHeight
    },
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 0,
    flexShrink: 0,
    textAlign: isCenteredUse ? 'center' : undefined,
    alignItems: 'center',
    paddingBlock: isModalH1Use ? theme.space['900'] : undefined,
    paddingBlockStart: isConversational ? theme.space['1000'] : isModalH1Use ? theme.space['900'] : undefined,
    paddingBlockEnd: isConversational ? theme.space['0'] : undefined,
    justifyContent: 'space-between',
    borderBottom: `${theme.borderWidth['transitional-2']} solid ${theme.color['border-container-default']}`
  }, getBackground(), $use === 'toolbar' && {
    padding: '0',
    borderBottom: 'none',
    backgroundColor: theme.color['fill-transparent-default']
  }, shouldHaveRoundedBorders(modalUse) && {
    borderRadius: `${theme.borderRadius['transitional-floating']} ${theme.borderRadius['transitional-floating']} ${theme.borderRadius['0']} ${theme.borderRadius['0']}`
  }, $cssStyles);
});
export const StyledHeaderInner = styled.div.withConfig({
  displayName: "DialogHeader__StyledHeaderInner"
})(({
  theme,
  $use
}) => {
  const modalUse = useContext(DialogContext).use;
  const isConversational = modalUse === 'conversational';
  const isSuccess = modalUse === 'success';
  const isDanger = modalUse === 'danger';
  const isInfo = modalUse === 'info';
  const isUpgrades = modalUse === 'upgrades';
  const isCentered = isConversational || isInfo || isUpgrades;
  const hasSmallerPadding = isSuccess || isDanger || isInfo || isUpgrades;
  return Object.assign({
    WebkitFontSmoothing: 'antialiased',
    display: 'flex',
    alignItems: 'center',
    paddingBlockStart: isConversational ? theme.space['0'] : theme.space['100'],
    paddingBlockEnd: isConversational ? theme.space['0'] : theme.space['100'],
    paddingInlineStart: theme.space['1000'],
    paddingInlineEnd: theme.space['1200'],
    justifyContent: isCentered ? 'center' : undefined,
    minHeight: isConversational ? theme.space['600'] : `calc(${theme.space['1400']} + ${theme.space['100']})`,
    width: '100%'
  }, hasSmallerPadding && {
    paddingInlineEnd: theme.space['1000']
  }, $use === 'toolbar' && {
    paddingInlineEnd: theme.space['600']
  }, {
    [`> ${StyledText}`]: {
      inlineSize: '100%',
      minInlineSize: 0
    },
    [`> * + ${StyledClickable}`]: {
      marginRight: theme.space['500']
    },
    [`> ${StyledClickable}:last-child`]: {
      marginRight: theme.space['0']
    },
    [`> h1, > h2, > h3, > h4, > h5, > h6`]: {
      margin: theme.space['0']
    },
    [`> h2`]: {
      marginBottom: isConversational ? theme.space['400'] : theme.space['0']
    }
  });
});
export const DialogHeader = _ref => {
  let {
      children,
      use
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(DialogHeaderContext.Provider, {
    value: {
      isDialogHeader: true
    },
    children: /*#__PURE__*/_jsx(StyledDialogHeader, Object.assign({
      $use: use
    }, domProps, {
      $cssStyles: cssStyles,
      children: /*#__PURE__*/_jsx(StyledHeaderInner, {
        $use: use,
        children: children
      })
    }))
  });
};
DialogHeader.displayName = 'DialogHeader';